import {convertHexToRGBA} from './color-utils';
import {toReadonly} from './typeHelpers';

export const mediaQueryMixins = {
  maxWidth: (n: string) => `@media (max-width: ${n})`,
  minWidth: (n: string) => `@media (min-width: ${n})`,
  between: (min: string, max: string) => `@media (min-width: ${min}) and (max-width: ${max})`
};

/**
 * Breakpoints & Media Queries
 */
export const tabletBreakpoint = 648;
export const desktopBreakpoint = 961;
export const menuBreakpoint = 1100;
export const desktopWideBreakpoint = 1271;

export type Breakpoint = 'desktop' | 'tablet' | 'mobile';

export function computeBreakpointFromWidth(width: number): Breakpoint {
  if (width > desktopBreakpoint)
    return 'desktop';

  if (width > tabletBreakpoint)
    return 'tablet';

  return 'mobile';
}

// Targeting the given screen size or bigger
export const mediaBreakpointUp = (device: 'tablet' | 'desktop' | 'desktop-wide') => {
  switch (device) {
    case 'tablet':
      return mediaQueryMixins.minWidth(`${tabletBreakpoint}px`);
    case 'desktop':
      return mediaQueryMixins.minWidth(`${desktopBreakpoint}px`);
    case 'desktop-wide':
      return mediaQueryMixins.minWidth(`${desktopWideBreakpoint}px`);
    default:
      return '';
  }
};

// Targeting the given screen size or smaller
export const mediaBreakpointDown = (device: 'mobile' | 'tablet' | 'desktop') => {
  switch (device) {
    case 'mobile':
      return mediaQueryMixins.maxWidth(`${tabletBreakpoint - 1}px`);
    case 'tablet':
      return mediaQueryMixins.maxWidth(`${desktopBreakpoint - 1}px`);
    case 'desktop':
      return mediaQueryMixins.maxWidth(`${desktopWideBreakpoint - 1}px`);
    default:
      return '';
  }
};

// Targeting a single segment of screen sizes
export const mediaBreakpointOnly = (device: 'tablet' | 'desktop') => {
  switch (device) {
    case 'tablet':
      return mediaQueryMixins.between(`${tabletBreakpoint}px`, `${desktopBreakpoint - 1}px`);
    case 'desktop':
      return mediaQueryMixins.between(`${desktopBreakpoint}px`, `${desktopWideBreakpoint - 1}px`);
    default:
      return '';
  }
};

export const mediaBreakpointBetween = (device1: 'tablet', device2: 'desktop-wide') => {
  let breakpoint1;
  let breakpoint2;

  switch (device1) {
    case 'tablet':
      breakpoint1 = tabletBreakpoint;
      break;
    default:
      return '';
  }
  switch (device2) {
    case 'desktop-wide':
      breakpoint2 = desktopWideBreakpoint;
      break;
    default:
      return '';
  }

  return mediaQueryMixins.between(`${breakpoint1}px`, `${breakpoint2 - 1}px`);
};

/**
 * Transitions.
 */
export const transition = {
  fast: '150ms'
};

/**
 * Fonts.
 */

export const fontWeights = toReadonly({
  thin: 100,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
});

export const fontFamiliesStrings = {
  title: `
  'Roobert',
  Helvetica,
  Arial,
  sans-serif
  `,
  body: `
  'Roobert',
  Helvetica,
  Arial,
  sans-serif
  `
};

export const fontSizesMixins = {
  h0: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 72px;
    line-height: 70px;
    font-weight: ${fontWeights.semibold};
  `,
  h1: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 56px;
    line-height: 53px;
    font-weight: ${fontWeights.semibold};
  `,
  h2: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 40px;
    line-height: 42px;
    font-weight: ${fontWeights.semibold};
  `,
  h3: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 24px;
    line-height: 30px;
    font-weight: ${fontWeights.semibold};
  `,
  h4: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 24px;
    line-height: 30px;
    font-weight: ${fontWeights.normal};
  `,
  h5: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 22px;
    line-height: 28px;
    font-weight: ${fontWeights.normal};
  `,
  h6: () => `
    font-family: ${fontFamiliesStrings.title};
    font-size: 20px;
    line-height: 26px;
    font-weight: ${fontWeights.normal};
  `,
  text1: () => `
    font-weight: ${fontWeights.normal};
    font-family: ${fontFamiliesStrings.body};
    font-size: 22px;
    line-height: 35px;
  `,
  text2: () => `
    font-weight: ${fontWeights.normal};
    font-family: ${fontFamiliesStrings.body};
    font-size: 18px;
    line-height: 29px;
  `,
  text2Spread: () => `
    font-weight: ${fontWeights.normal};
    font-family: ${fontFamiliesStrings.body};
    font-size: 18px;
    line-height: 32px;
  `,
  text3: () => `
    font-weight: ${fontWeights.normal};
    font-family: ${fontFamiliesStrings.body};
    font-size: 16px;
    line-height: 26px;
  `,
  text4: () => `
    font-weight: ${fontWeights.normal};
    font-family: ${fontFamiliesStrings.body};
    font-size: 14px;
    line-height: 24px;
  `
};

/**
 * Colors.
 */

interface PaletteColor {
  readonly base: string;
  readonly highlight: string;
}

export enum PaletteColorsEnum {
  BLACK = 'black',
  BLUE = 'blue',
  AQUA = 'aqua',
  GREEN = 'green',
  YELLOW = 'yellow',
  ORANGE = 'orange',
  RED = 'red',
  FRONTIER_RED = 'frontier_red',
  PINK = 'pink',
  VIOLET = 'violet',
  WHITE = 'white'
}

type Palette = {readonly [T in PaletteColorsEnum]: PaletteColor};

export const palette: Palette = {
  [PaletteColorsEnum.BLACK]: {
    base: '#001B38',
    highlight: '#282828'
  },
  [PaletteColorsEnum.BLUE]: {
    base: '#0090DA',
    highlight: '#E6F4FC'
  },
  [PaletteColorsEnum.AQUA]: {
    base: '#00DCC0',
    highlight: '#CEF7F2'
  },
  [PaletteColorsEnum.GREEN]: {
    base: '#28AC2F',
    highlight: '#D2F4B7'
  },
  [PaletteColorsEnum.YELLOW]: {
    base: '#F3CE2A',
    highlight: '#FDF8DF'
  },
  [PaletteColorsEnum.ORANGE]: {
    base: '#F36D0C',
    highlight: '#FFF1E7'
  },
  [PaletteColorsEnum.RED]: {
    base: '#FF0C0C',
    highlight: '#FFDBDB'
  },
  [PaletteColorsEnum.FRONTIER_RED]: {
    base: '#FF0C64',
    highlight: '#FF0C64'
  },
  [PaletteColorsEnum.PINK]: {
    base: '#FF38C7',
    highlight: '#FFDBF5'
  },
  [PaletteColorsEnum.VIOLET]: {
    base: '#8727E9',
    highlight: '#E7D4FB'
  },
  [PaletteColorsEnum.WHITE]: {
    base: '#F3F2F0',
    highlight: '#FFF'
  }
};

export const paletteAlphas = {
  black5: convertHexToRGBA(palette.black.base, 0.05),
  black8: convertHexToRGBA(palette.black.base, 0.08),
  black10: convertHexToRGBA(palette.black.base, 0.1),
  black20: convertHexToRGBA(palette.black.base, 0.2),
  black40: convertHexToRGBA(palette.black.base, 0.4),
  black80: convertHexToRGBA(palette.black.base, 0.8)
};
